import React, { ReactNode } from 'react';
import { formatCurrency } from '@wavvest/shared-utils';

export const linkedAccountsTotals = (
  cashTotal: number,
  owedTotal: number,
  investmentTotal?: number,
) => ({
  netTotal: formatCurrency(cashTotal + (investmentTotal ?? 0) - owedTotal),
  cashLine: `${formatCurrency(cashTotal)} cash${
    investmentTotal !== undefined ? ` · ${formatCurrency(investmentTotal)} investments` : ''
  }${owedTotal > 0 ? ` · ${formatCurrency(owedTotal)} owed` : ''}`,
  cashTotal: formatCurrency(cashTotal),
  investmentTotal: formatCurrency(investmentTotal ?? 0),
  liabilityTotal: `${formatCurrency(owedTotal)} owed`,
});

export const linkedAccountFundsLabel = (fundedCount: number): string =>
  fundedCount > 0 ? `${fundedCount} ${fundedCount === 1 ? 'account' : 'accounts'}` : '—';

export const linkedAccountTypeLabel = (accountType: string, mask?: string | null): string =>
  `${accountType || 'Account'}${mask ? ` ••${mask}` : ''}`;

export type LinkedAccountRow = {
  id: string;
  title: ReactNode;
  mark: ReactNode;
  type: ReactNode;
  amount: string;
  funds?: string;
  status?: ReactNode;
  actions?: ReactNode;
};

export type LinkedAccountsTableProps = {
  netTotal?: string;
  cashLine?: string;
  cashTotal?: string;
  cashRows: LinkedAccountRow[];
  investmentTotal?: string;
  investmentRows?: LinkedAccountRow[];
  liabilityTotal?: string;
  liabilityRows?: LinkedAccountRow[];
  footer?: ReactNode;
  empty?: ReactNode;
};

const Columns = ({
  amountLabel,
  showFunds,
  showActions,
}: {
  amountLabel: string;
  showFunds: boolean;
  showActions: boolean;
}) => (
  <div className="linked-accounts-columns">
    <span>Account</span>
    <span>Type</span>
    <span>{amountLabel}</span>
    <span>{showFunds ? 'Funds' : ''}</span>
    <span>Status</span>
    <span>{showActions ? 'Actions' : ''}</span>
  </div>
);

const Row = ({ row }: { row: LinkedAccountRow }) => (
  <div className="linked-accounts-row">
    <div className="linked-accounts-name">
      {row.mark}
      <span>{row.title}</span>
    </div>
    <span className="linked-accounts-type">{row.type}</span>
    <span className="linked-accounts-money">{row.amount}</span>
    <span>{row.funds ?? ''}</span>
    <span className="linked-accounts-status">{row.status}</span>
    {row.actions ?? <span />}
  </div>
);

const LinkedAccountsTable: React.FC<LinkedAccountsTableProps> = ({
  netTotal,
  cashLine,
  cashTotal,
  cashRows,
  investmentTotal,
  investmentRows = [],
  liabilityTotal,
  liabilityRows = [],
  footer,
  empty,
}) => {
  const showActions = [...cashRows, ...investmentRows, ...liabilityRows].some((row) => row.actions);
  return (
    <section className="linked-accounts" aria-label="Linked accounts">
      <header>
        <div>
          <h2>Linked accounts</h2>
          <p>External financial accounts</p>
        </div>
        {netTotal ? (
          <div className="linked-accounts-net">
            <strong>{netTotal}</strong>
            {cashLine ? <span>{cashLine}</span> : null}
          </div>
        ) : null}
      </header>
      {cashRows.length === 0 && investmentRows.length === 0 && liabilityRows.length === 0
        ? empty
        : null}
      {cashRows.length > 0 && (
        <div className="linked-accounts-section" role="group" aria-label="Cash accounts">
          <div className="linked-accounts-group">
            <h3>Cash accounts</h3>
            {cashTotal ? <span>{cashTotal}</span> : null}
          </div>
          <Columns amountLabel="Balance" showFunds showActions={showActions} />
          {cashRows.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </div>
      )}
      {investmentRows.length > 0 && (
        <div className="linked-accounts-section" role="group" aria-label="Investment accounts">
          <div className="linked-accounts-group">
            <h3>Investment accounts</h3>
            {investmentTotal ? <span>{investmentTotal}</span> : null}
          </div>
          <Columns amountLabel="Balance" showFunds={false} showActions={showActions} />
          {investmentRows.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </div>
      )}
      {liabilityRows.length > 0 && (
        <div className="linked-accounts-section" role="group" aria-label="Liabilities">
          <div className="linked-accounts-group is-liabilities">
            <h3>Liabilities</h3>
            {liabilityTotal ? <span>{liabilityTotal}</span> : null}
          </div>
          <Columns amountLabel="Owed" showFunds={false} showActions={showActions} />
          {liabilityRows.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </div>
      )}
      {footer}
    </section>
  );
};

export default LinkedAccountsTable;
